import React, { useEffect, useState } from 'react'
import infoSvg from '../icons/blankslate-info-icon.svg'
import {
  BlankSlate,
  PrimaryActionButton,
  Description,
} from '@cjdev/visual-stack/lib/components/BlankSlate'
import '@cjdev/visual-stack/lib/css/base.css'
import '@cjdev/visual-stack/lib/css/bootstrap-cj.css'
import '@cjdev/visual-stack/lib/css/forms.css'
import '@cjdev/visual-stack/lib/css/layout.css'
import '@cjdev/visual-stack/lib/css/globalVariables.css'
import { getInvitationByIdentity, resendInvitation } from '../../../api/federatedUserInvitations'


/**
 * Error component for unauthorized federated users awaiting email verification.
 * Fetches invitation status and displays appropriate messaging with resend option.
 *
 * @param {Object} props
 * @param {Object} props.data - Parsed context data containing connection and externalId
 * @param {Object} props.history - React Router history object
 */
export const FederatedUserUnauthorized = ({ data = {}, history }) => {
  const { connection, externalId } = data
  const [invitationData, setInvitationData] = useState(null)
  const [fetchError, setFetchError] = useState(false)
  const [loading, setLoading] = useState(true)
  const [resending, setResending] = useState(false)
  const [resendSuccess, setResendSuccess] = useState(false)
  const [resendError, setResendError] = useState(null)


  const connectionName = connection || 'your identity provider'

  // Fetch invitation status on mount
  useEffect(() => {
    async function fetchInvitationStatus() {
      const result = await getInvitationByIdentity(connection, externalId)

      if (result.error) {
        setFetchError(true)
      } else {
        setInvitationData(result.data)
        setFetchError(false)
      }

      setLoading(false)
    }

    fetchInvitationStatus()
  }, [connection, externalId])

  // Handle resend email request
  const handleResendEmail = async () => {
    setResending(true)
    setResendError(null)

    const result = await resendInvitation(connection, externalId)

    if (result.error) {
      setResendError(result.error)
    } else {
      setResendSuccess(true)
    }

    setResending(false)
  }

  if (loading) {
    return (
      <BlankSlate
        title="Loading..."
        headerGraphic={
          <img src={infoSvg} alt="info graphic" className="headerGraphic" />
        }
      >
        <Description>Checking invitation status...</Description>
      </BlankSlate>
    )
  }

  // Fetch error state - unable to verify invitation
  if (fetchError) {
    return (
      <BlankSlate
        title="Invitation Status:"
        headerGraphic={
          <img src={infoSvg} alt="error graphic" className="headerGraphic" />
        }
      >
        <Description>
          Unable to verify invitation status. Please try again or contact your support team for assistance.
        </Description>
      </BlankSlate>
    )
  }

  // No invitation found state
  if (!invitationData) {
    return (
      <BlankSlate
        title="Invitation Status:"
        headerGraphic={
          <img src={infoSvg} alt="error graphic" className="headerGraphic" />
        }
      >
        <Description>
          No pending invitation found. Please contact your support team for assistance.
        </Description>
      </BlankSlate>
    )
  }

  // Invitation found state - show resend button
  return (
    <BlankSlate
      title="Invitation Status: Pending"
      headerGraphic={
        <img src={infoSvg} alt="info graphic" className="headerGraphic" />
      }
    >
      <Description>
        Please check your email to approve the federated user authorization for {connectionName}.
      </Description>
      {resendSuccess ? (
        <Description>Email sent! Check your inbox.</Description>
      ) : (
        <PrimaryActionButton
          label={resending ? 'Sending...' : 'Resend Email'}
          handler={handleResendEmail}
          disabled={resending}
        />
      )}
      {resendError && (
        <Description style={{ color: 'red' }}>
          {resendError}
        </Description>
      )}
    </BlankSlate>
  )
}
